import { BsFillExclamationCircleFill as WarningIcon } from 'react-icons/bs';
import { Modal } from 'reactstrap';
import styles from './DebtCard.module.scss';
import MeritoButton from '../common/MeritoButtons/MeritoButtons';
import { DebtDocument, debtType } from '../../interfaces/interfaces';
import { getDebtTypeLabel } from './debtHelpers';

// what the confirmation popup is asking about
export type PendingConfirmation =
    | { kind: 'removeSanctionLetter'; document: DebtDocument }
    | { kind: 'deleteDebt' }
    | { kind: 'changeDebtType'; fromDebtType: debtType; newDebtType: debtType };

type Props = {
    // the action waiting on the user's answer; null keeps the popup closed
    pending: PendingConfirmation | null;
    // a separate schedule file goes with the sanction letter, so the wording says so
    hasSeparateSchedule: boolean;
    // an action is running: the popup can't be dismissed and Confirm shows a loader
    isBusy: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

// wording for whichever action the popup is confirming
const getConfirmationCopy = (pending: PendingConfirmation | null, hasSeparateSchedule: boolean) => {
    switch (pending?.kind) {
        case 'deleteDebt':
            return {
                title: 'Delete Debt?',
                message: "You'll need to re-enter this loan's details from scratch.",
                confirmText: 'Delete',
            };
        case 'changeDebtType':
            return {
                title: 'Change loan type?',
                message: `You're changing the debt type from ${getDebtTypeLabel(pending.fromDebtType)} to ${getDebtTypeLabel(pending.newDebtType)}. Confirm to continue.`,
                confirmText: 'Confirm',
            };
        default:
            return {
                title: 'Remove sanction letter?',
                message: hasSeparateSchedule
                    ? "You'll need to re-enter this loan's details from scratch, including the repayment schedule."
                    : "You'll need to re-enter this loan's details from scratch.",
                confirmText: hasSeparateSchedule ? 'Remove Both' : 'Remove',
            };
    }
};

// The warning popup before deleting a debt, removing its sanction letter (both
// wipe the debt) or changing its loan type (keeps it).
const DebtConfirmationModal = ({ pending, hasSeparateSchedule, isBusy, onCancel, onConfirm }: Props) => {
    const copy = getConfirmationCopy(pending, hasSeparateSchedule);
    return (
        <Modal
            isOpen={pending != null}
            toggle={() => {
                // keep the popup up while the action is in flight
                if (!isBusy) onCancel();
            }}
            centered={true}
            style={{ width: '450px' }}
            contentClassName="shortcut_modal"
        >
            <div className={styles.confirmation}>
                <div className={styles.confirmationTop}>
                    <div className={styles.confirmationIcon}>
                        <WarningIcon />
                    </div>
                    <div className={styles.confirmationTitle}>{copy.title}</div>
                    <div className={styles.confirmationMessage}>{copy.message}</div>
                </div>
                <div className={styles.confirmationActions}>
                    <MeritoButton
                        btnText="Cancel"
                        type="secondary-2"
                        size="lg"
                        isRounded={true}
                        disabled={isBusy}
                        onClick={onCancel}
                    />
                    <MeritoButton
                        btnText={copy.confirmText}
                        type="primary-1"
                        size="lg"
                        isRounded={true}
                        loading={isBusy}
                        disabled={isBusy}
                        onClick={onConfirm}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default DebtConfirmationModal;
